import { useEffect, useMemo, useRef, useState } from 'react';
import { ContentfulEntry } from '@jbhi-fi/apis/src/contentful/types';
import { ContentfulFilter } from './useContentfulEntries/useContentfulEntries';
import { usePublicApiClient } from './usePublicApiClient';

export type UseCardEntriesProps = {
  filters: Array<ContentfulFilter>;
  page: number;
  pageSize: number;
  includeDepth?: number;
  appendResults?: boolean;
  enabled?: boolean;
  logPrefix?: string;
};

export type UseCardEntries = {
  entries: Array<ContentfulEntry<any>>;
  totalResults: number;
  isLoading: boolean;
};

export const useCardEntries = ({
  filters,
  page,
  pageSize,
  includeDepth = 10,
  appendResults = false,
  enabled = true,
  logPrefix = 'useCardEntries',
}: UseCardEntriesProps): UseCardEntries => {
  const [entries, setEntries] = useState<Array<ContentfulEntry<any>>>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(() => enabled && !!filters?.length && pageSize > 0);
  const { contentfulClient, contentfulThemeApiClient } = usePublicApiClient();
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const previousFiltersKey = useRef<string>(filtersKey);

  useEffect(() => {
    if (filtersKey === previousFiltersKey.current) return;
    previousFiltersKey.current = filtersKey;
    setEntries([]);
    setTotalResults(0);
  }, [filtersKey]);

  useEffect(() => {
    if (!enabled || !filters?.length || pageSize <= 0) {
      setEntries([]);
      setTotalResults(0);
      setIsLoading(false);
      return;
    }

    const isPreview = window.Jbhifi?.isContentPreview || false;
    const shouldUseThemeApi = isPreview || !contentfulClient;
    const client = shouldUseThemeApi ? contentfulThemeApiClient : contentfulClient;

    if (!client) {
      setEntries([]);
      setTotalResults(0);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    const skip = (page - 1) * pageSize;

    const fetchEntries = async () => {
      setIsLoading(true);
      try {
        const results = await Promise.all(
          filters.map(filter =>
            client.getEntries({
              ...filter,
              limit: pageSize.toString(),
              include: includeDepth.toString(),
              skip: skip.toString(),
            })
          )
        );

        if (isCancelled) return;

        results.forEach((result, index) => {
          if (result.error) {
            console.warn(`${logPrefix} response included errors:`, result.error, filters[index]);
          }
        });

        const fetchedEntries = results.reduce<Array<ContentfulEntry<any>>>((acc, result) => {
          return result.data?.items ? acc.concat(result.data.items as Array<ContentfulEntry<any>>) : acc;
        }, []);
        const fetchedTotalResults = results.reduce((acc, result) => acc + (result.data?.total ?? 0), 0);

        setEntries(currentEntries =>
          appendResults && page > 1 ? currentEntries.concat(fetchedEntries) : fetchedEntries
        );
        setTotalResults(fetchedTotalResults);
      } catch (error) {
        console.error(`${logPrefix} error:`, error);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchEntries();

    return () => {
      isCancelled = true;
    };
  }, [
    appendResults,
    contentfulClient,
    contentfulThemeApiClient,
    enabled,
    filtersKey,
    includeDepth,
    logPrefix,
    page,
    pageSize,
  ]);

  return { entries, totalResults, isLoading };
};
